import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../layouts/TopBar/topBar';
import { Spinner } from '../../ui/atoms';
import { createPodcast, describePodcastError } from './podcastApi';
import usePodcastConfig from './usePodcastConfig';
import PodcastForm from './PodcastForm';
import { PODCASTS_PATH } from './podcastJob';

const PodcastCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config, loading, fallback } = usePodcastConfig();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createPodcast(payload);
      if (!mountedRef.current) return;
      if (!created || !created.id) throw new Error('');
      navigate(`${PODCASTS_PATH}/${created.id}`);
    } catch (error) {
      if (mountedRef.current) {
        setSubmitError(describePodcastError(error, t));
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="bg-transparent min-h-full">
      <TopBar onGoBack={() => navigate(PODCASTS_PATH)} mode="back-only" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mt-6 mb-8">
          <h1 className="text-3xl font-medium leading-tight text-primary-900">{t('podcasts.create.title')}</h1>
          <p className="mt-4 text-base text-primary-900 opacity-60 max-w-4xl">{t('podcasts.create.description')}</p>
        </div>

        {loading || !config ? (
          <div className="flex justify-center py-24" data-testid="podcast-config-loading">
            <Spinner size="lg" label={t('podcasts.create.loadingConfig')} />
          </div>
        ) : (
          <>
            {fallback && (
              <div role="status" className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                {t('podcasts.create.configFallback')}
              </div>
            )}
            <PodcastForm config={config} submitting={submitting} onSubmit={handleSubmit} />
            {submitError && (
              <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="podcast-submit-error">
                <p className="font-medium">{t('podcasts.create.submitError')}</p>
                <p className="mt-1">{submitError}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PodcastCreatePage;
